// frontend/src/utils/conversation/buildObjectiveDecisionState.js

function normalizeText(value = '') {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ');
}

function countWords(value = '') {
  const normalized = normalizeText(value);
  if (!normalized) return 0;
  return normalized.split(/\s+/).filter(Boolean).length;
}

function isQuestionLike(message = '') {
  const lower = normalizeText(message);
  if (!lower) return false;
  if (String(message || '').includes('?')) return true;

  return /^(what|why|how|when|where|who|can you|could you|would you|will you|do you|did you|are you|is it|should i|should we)\b/.test(
    lower
  );
}

function cloneObjective(value = null) {
  if (!value || typeof value !== 'object') return null;
  return { ...value };
}

function normalizeReasoningChain(value = []) {
  if (!Array.isArray(value)) return [];

  return value
    .filter(Boolean)
    .map((item) => {
      if (typeof item === 'string') {
        return {
          text: item,
          source: 'legacy_string',
        };
      }

      if (typeof item === 'object') {
        return {
          ...item,
          text: item.text || item.statement || '',
        };
      }

      return null;
    })
    .filter((item) => item?.text)
    .slice(-8);
}

function detectObjectiveExit(message = '') {
  const lower = normalizeText(message);

  if (!lower) return null;

  const explicitExitPatterns = [
    /\bnever mind\b/,
    /\bdifferent question\b/,
    /\bnew question\b/,
    /\bnew topic\b/,
    /\bforget that\b/,
    /\bswitch(?:ing)? (?:topics?|gears)\b/,
    /\bchange (?:topics?|subjects?)\b/,
    /\bseparate question\b/,
  ];

  const matched = explicitExitPatterns.find((pattern) => pattern.test(lower));

  if (!matched) return null;

  return {
    explicit: true,
    confidence: 0.99,
    reason: 'explicit_user_exit',
    pattern: matched.toString(),
  };
}

function detectDecisionRequest(message = '') {
  const lower = normalizeText(message);

  if (!lower) return null;

  const decisionPatterns = [
    {
      pattern: /\b(should i|should we|do i|do we)\b.*\b(comment|reply|respond|engage|post|publish|share|send|buy|sell|call|email|apply|move|fix|build|add|remove|change)\b/,
      decision: 'Choose whether to take the next action',
      objective: 'Determine whether the action is worthwhile',
    },
    {
      pattern: /\b(worth|worthwhile|worth my time|worth doing|good idea|bad idea)\b/,
      decision: 'Proceed or do not proceed',
      objective: 'Evaluate whether the opportunity is worthwhile',
    },
    {
      pattern: /\b(decide|decision|figure out|work out|determine)\b/,
      decision: 'Resolve the active decision',
      objective: 'Determine the best next move',
    },
  ];

  const matched = decisionPatterns.find((entry) => entry.pattern.test(lower));
  if (!matched) return null;

  return {
    activeObjective: {
      text: matched.objective,
      source: 'message_decision_signal',
      confidence: 0.82,
    },
    activeDecision: {
      text: matched.decision,
      source: 'message_decision_signal',
      confidence: 0.82,
    },
  };
}

function detectEvidenceFragment(message = '') {
  const lower = normalizeText(message);
  const words = countWords(lower);

  if (!lower) return null;
  if (isQuestionLike(message)) return null;
  if (words > 14) return null;

  const evidenceSignals = [
    /\b\d[\d,.]*(k|m|%)?\b/,
    /\b(followers|views|likes|comments|shares|audience|engagement|traffic|reach)\b/,
    /\b(interesting|that matters|that changes|decent|huge|small|not bad|good sign|bad sign)\b/,
    /\b(so|then|therefore|meaning|which means)\b/,
  ];

  const signalCount = evidenceSignals.reduce(
    (count, pattern) => count + (pattern.test(lower) ? 1 : 0),
    0
  );

  if (signalCount <= 0) return null;

  return {
    text: String(message || '').trim(),
    source: 'evidence_fragment',
    confidence: signalCount >= 2 ? 0.86 : 0.7,
    createdAt: new Date().toISOString(),
  };
}

function inferObjectiveFromTopic({ topic = null, thread = null } = {}) {
  const topicText = topic?.topic || topic?.label || thread?.thread || '';
  const lower = normalizeText(topicText);

  if (!lower) return null;

  if (/linkedin|post|comment|thread|audience|engagement/.test(lower)) {
    return {
      activeObjective: {
        text: 'Determine whether this thread is worth engaging',
        source: 'topic_objective_inference',
        confidence: 0.64,
      },
      activeDecision: {
        text: 'Comment or ignore',
        source: 'topic_objective_inference',
        confidence: 0.64,
      },
    };
  }

  return null;
}

function buildObjectiveDecisionState({
  message = '',
  currentObjective = null,
  currentDecision = null,
  currentReasoningChain = [],
  lastReferencedTopic = null,
  lastReferencedThread = null,
  isFreshOpen = false,
} = {}) {
  if (isFreshOpen) {
    return {
      activeObjective: null,
      activeDecision: null,
      activeReasoningChain: [],
      detectedEvidenceFragment: null,
      detectedDecisionRequest: null,
      detectedObjectiveExit: null,
      signals: {
        objectiveDetected: false,
        decisionDetected: false,
        evidenceFragmentDetected: false,
        carriedForward: false,
        inferredFromTopic: false,
        objectiveExited: false,
      },
    };
  }

  const existingObjective = cloneObjective(currentObjective);
  const existingDecision = cloneObjective(currentDecision);
  const existingChain = normalizeReasoningChain(currentReasoningChain);

  const detectedObjectiveExit = detectObjectiveExit(message);
  const shouldResetObjective = detectedObjectiveExit?.explicit === true;

  const detectedDecisionRequest = shouldResetObjective
    ? null
    : detectDecisionRequest(message);

  const inferredFromTopic = shouldResetObjective
    ? null
    : inferObjectiveFromTopic({
        topic: lastReferencedTopic,
        thread: lastReferencedThread,
      });

  const detectedEvidenceFragment = shouldResetObjective
    ? null
    : detectEvidenceFragment(message);

  const activeObjective = shouldResetObjective
    ? null
    : detectedDecisionRequest?.activeObjective ||
      existingObjective ||
      inferredFromTopic?.activeObjective ||
      null;

  const activeDecision = shouldResetObjective
    ? null
    : detectedDecisionRequest?.activeDecision ||
      existingDecision ||
      inferredFromTopic?.activeDecision ||
      null;

  const shouldAttachEvidence =
    !shouldResetObjective &&
    !!detectedEvidenceFragment &&
    (!!activeObjective || !!activeDecision || existingChain.length > 0);

  const activeReasoningChain = shouldResetObjective
    ? []
    : shouldAttachEvidence
      ? [
          ...existingChain,
          {
            ...detectedEvidenceFragment,
            role: 'evidence',
          },
        ].slice(-8)
      : existingChain;

  return {
    activeObjective,
    activeDecision,
    activeReasoningChain,
    detectedEvidenceFragment: shouldAttachEvidence ? detectedEvidenceFragment : null,
    detectedDecisionRequest,
    detectedObjectiveExit,
    signals: {
      objectiveDetected: !!activeObjective,
      decisionDetected: !!activeDecision,
      evidenceFragmentDetected: shouldAttachEvidence,
      carriedForward:
        !shouldResetObjective &&
        !detectedDecisionRequest &&
        (!!existingObjective || !!existingDecision),
      inferredFromTopic:
        !shouldResetObjective &&
        !!inferredFromTopic &&
        !existingObjective &&
        !existingDecision,
      objectiveExited: shouldResetObjective,
    },
  };
}

export default buildObjectiveDecisionState;